import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Force every route transition (and initial mount) to start at the very top.
 *
 * Handles three things the browser gets wrong on an SPA:
 *   1. The window scroll position (public pages).
 *   2. The internal `<main>` scroll container used by AdminLayout & similar
 *      layouts — these use `overflow-auto`, which puts their own scrollTop
 *      outside window's reach.
 *   3. The browser's automatic scroll restoration on back/forward navigation,
 *      which otherwise re-applies the previous scrollY after we reset it.
 *
 * In-page anchor navigation (`#hash`) is respected — we only reset when the
 * hash is empty, so on-page hash links keep working.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Disable the browser's scroll restoration once — otherwise on back/forward
    // it will snap the scroll position back after our reset runs.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (hash) return;

    const resetAll = () => {
      // Cross-browser: hit both roots.
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      // Any internal scroll container (e.g. AdminLayout's <main class="overflow-auto">).
      document.querySelectorAll("main, [data-scroll-container]").forEach((el) => {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      });
    };

    // Reset synchronously on route change...
    resetAll();
    // ...and again on the next frame to defeat any late layout shift
    // (images/fonts loading, lazy-mounted heavy components).
    const raf = requestAnimationFrame(resetAll);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
